import { z } from 'zod';

import { addressInputSchema, addressSchema } from './address';
import {
  emailSchema,
  isoDateTimeSchema,
  phoneSchema,
  ulidSchema,
  userIdSchema,
} from './primitives';

export const savedUserAddressEntitySchema = addressSchema.extend({
  addressId: ulidSchema,
  label: z.string().min(1).max(120),
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema,
});
export type SavedUserAddressEntity = z.infer<typeof savedUserAddressEntitySchema>;

export const userEntitySchema = z.object({
  userId: userIdSchema,
  email: emailSchema,
  phone: phoneSchema.optional(),
  displayName: z.string().min(1).max(140),
  locale: z.string().min(2).max(8).default('en'),
  defaultDeliveryAddress: addressSchema.optional(),
  savedAddresses: z.array(savedUserAddressEntitySchema).default([]),
  marketingPrefs: z
    .object({
      emailOffers: z.boolean().default(false),
      smsOffers: z.boolean().default(false),
    })
    .default({ emailOffers: false, smsOffers: false }),
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema,
});
export type UserEntity = z.infer<typeof userEntitySchema>;

export const savedUserAddressSelfSchema = savedUserAddressEntitySchema.omit({
  createdAt: true,
  updatedAt: true,
});
export type SavedUserAddressSelf = z.infer<typeof savedUserAddressSelfSchema>;

export const userSelfSchema = z.object({
  userId: userIdSchema,
  email: emailSchema,
  phone: phoneSchema.optional(),
  displayName: z.string(),
  locale: z.string(),
  defaultDeliveryAddress: addressSchema.optional(),
  savedAddresses: z.array(savedUserAddressSelfSchema),
  marketingPrefs: z.object({
    emailOffers: z.boolean(),
    smsOffers: z.boolean(),
  }),
});
export type UserSelf = z.infer<typeof userSelfSchema>;

export const patchMeRequestSchema = z
  .object({
    displayName: z.string().min(1).max(140).optional(),
    phone: phoneSchema.optional(),
    locale: z.string().min(2).max(8).optional(),
    marketingPrefs: z
      .object({
        emailOffers: z.boolean().optional(),
        smsOffers: z.boolean().optional(),
      })
      .optional(),
    /** Replaces `defaultDeliveryAddress` after server geocoding. Omit to leave unchanged. */
    defaultDeliveryAddress: addressInputSchema.optional().nullable(),
  })
  .strict()
  .refine((b) => Object.values(b).some((v) => v !== undefined), {
    message: 'At least one field is required',
  });
export type PatchMeRequest = z.infer<typeof patchMeRequestSchema>;

export const postSavedAddressRequestSchema = z.object({
  label: z.string().min(1).max(120),
  address: addressInputSchema,
  defaultForDelivery: z.boolean().optional(),
});
export type PostSavedAddressRequest = z.infer<typeof postSavedAddressRequestSchema>;

export const patchSavedAddressRequestSchema = z
  .object({
    label: z.string().min(1).max(120).optional(),
    address: addressInputSchema.optional(),
    defaultForDelivery: z.boolean().optional(),
  })
  .strict()
  .refine((b) => Object.values(b).some((v) => v !== undefined), {
    message: 'At least one field is required',
  });
export type PatchSavedAddressRequest = z.infer<typeof patchSavedAddressRequestSchema>;

export const savedAddressPathParamsSchema = z.object({
  addressId: ulidSchema,
});
export type SavedAddressPathParams = z.infer<typeof savedAddressPathParamsSchema>;

export const userAdminSchema = userSelfSchema.extend({
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema,
});
export type UserAdmin = z.infer<typeof userAdminSchema>;
